// ─────────────────────────────────────────────
//  optionCopy — WHAT each quick-action says and does. Its sibling
//  `domain/assignChoices` decides WHICH of them appear.
//
//  ⚠️ THAT SPLIT IS NOT TIDINESS, IT IS THE FIX FOR A REAL DEFECT. The menu once
//  offered the second provider "Assign Supervising CRNA — swap Kayla Gibbons for
//  somebody else beside Kayla Gibbons", one string trying to be both a rule and
//  a sentence, whose write would have replaced the very person the operator had
//  clicked. The rule is a three-position enum with a test; this is copy. Neither
//  file can quietly become the other.
//
//  ⚠️ THE EXPLANATION LINE IS NOT DECORATION. Every option here MOVES a real
//  person or REWRITES a clinical fact, and several are irreversible from this
//  menu. The `sub` is where the consequence is stated, in the operator's words
//  and not the schema's.
// ─────────────────────────────────────────────
import {
  NEW_ROOM, TAKEOVER, SECOND_CRNA, EDIT_CRNA, UNASSIGN, BREAKS, HOME, TRANSFER, BENCH,
} from "../../domain/assignChoices";
import { IDENTITY_AMBIGUOUS } from "../../domain/secondProviderWrite";

/**
 * WHY this menu is short, in the operator's words — or "" when it is not short.
 *
 * ⚠️ A MENU THAT SILENTLY OFFERS LESS IS THE SAME DEFECT AS A NAME THAT OPENS
 * NOTHING, IN A SMALLER FORM. That is exactly how the owner read it: *"why
 * taylor prentice, kayla, briana, larson … have only one … all the crna need to
 * have the same privileges"*. Almost every one of those is now resolved by name
 * and gets the identical menu to anybody else. What is left over is the case
 * where the app genuinely cannot tell WHICH staff record a name belongs to, and
 * for that the answer is to say so — a refusal the operator can read and act on
 * beats a shorter list they can only wonder about.
 *
 * ⚠️ COPY ONLY. Whether a write may happen is `isResolvedProvider`, decided in
 * `domain/secondProviderWrite` and guarded again at each write. Nothing in this
 * function is allowed to become that rule.
 */
export function identityNote({ provider, roomLabel = "the room" } = {}) {
  // A provider with a usable id has nothing to explain — including every bench
  // entry, which never carries an `identity` at all.
  if (provider?.id) return "";
  const name = provider?.name || "This CRNA";
  if (provider?.identity?.status === IDENTITY_AMBIGUOUS) {
    const n = provider.identity.count || 2;
    return (
      `${n} staff records are named ${name}, so this board cannot tell which one is in ` +
      `${roomLabel}. Only the leaving time can be changed here — anything that moves ` +
      `${name} would have to pick a record, and picking the wrong one puts this room on ` +
      `somebody else's day. Ask an administrator to merge the duplicates.`
    );
  }
  return (
    `${name} is not matched to a staff record — the schedule cell they came from named ` +
    `them in text and nothing else. Only the leaving time can be changed here, because it ` +
    `belongs to the room's row rather than to a person. Ask an administrator to add ` +
    `${name} to Staff and the rest of this menu appears on its own.`
  );
}

export default function optionCopy({
  provider,
  roomLabel = "the room",
  givingBreaks = false,
  // Where this person is standing. Only the BREAKS wording reads it: the same
  // option means "stay on the free list" to the bench and "keep your room and
  // be listed as a giver" to somebody holding one, and one string cannot be
  // both without lying to one of them.
  position = BENCH,
  // A `secondProviderOf` result when somebody already stands beside this
  // provider, else null. Only changes the SECOND_CRNA wording.
  existingSecond = null,
  // Who HOLDS the room this provider is standing in, already peeled off the
  // seat cell. Only `UNASSIGN` reads it, and only to promise that the room is
  // not left empty — the sentence is the whole point of the option.
  seatName = "",
  // The leaving time this row RECORDS for this person, already in the 12-hour
  // spelling the board speaks ("3 PM"), or "" when none is recorded.
  departLabel = "",
  actions = {},
}) {
  const name = provider?.name || "them";
  return {
    [NEW_ROOM]: {
      icon: "🚪",
      label: "New Room",
      sub: "Place in a room — pick room, times, and MD.",
      onPick: actions.onNewRoom,
    },
    [TAKEOVER]: {
      icon: "🔁",
      label: "Takeover",
      sub: "Replace the CRNA in a room they're already running.",
      onPick: actions.onTakeover,
    },
    [SECOND_CRNA]: {
      icon: "👥",
      label: "Assign Supervising CRNA",
      // ⚠️ ALWAYS "alongside", NEVER "swap". This option is offered ONLY from
      // the seat position, so the person beside them is somebody else by
      // definition; the earlier "Swap X for somebody else beside X" wording was
      // the menu opened on the second provider herself, and its write replaced
      // her.
      sub: existingSecond
        ? `Swap ${existingSecond.name} for somebody else beside ${name} in ${roomLabel}.`
        : `Put a second CRNA / AA in ${roomLabel} alongside ${name}.`,
      onPick: actions.onSecondCrna,
    },
    [EDIT_CRNA]: {
      icon: "🕒",
      // ⚠️ THE OWNER'S PHRASE ON THE LABEL, THE CONSEQUENCE ON THE LINE BELOW.
      // He asked for "edit crna, inside you can change the leaving time"; the
      // panel changes a leaving time and nothing else, so the sub-line says so
      // rather than letting "Edit CRNA" imply it edits the person.
      label: "Edit CRNA",
      sub: departLabel
        ? `Change when ${name} leaves ${roomLabel} — currently ${departLabel}. Nothing else about them changes.`
        : `Set when ${name} leaves ${roomLabel} — none is recorded. Nothing else about them changes.`,
      onPick: actions.onEditDepart,
    },
    [UNASSIGN]: {
      icon: "↩️",
      // The owner's word. It is the name of the list they land on, which is why
      // it beats "Remove" — nobody is removed from the day, they are taken out
      // of a room and are available again.
      label: "Unassigned",
      // ⚠️ TWO FACTS, BOTH LOAD-BEARING. Where the person goes (still on today's
      // roster, back on the free list — NOT home, which is the option below and
      // a different day for them), and what happens to the ROOM. An operator
      // pressing this on a two-CRNA room has to know it does not empty it.
      sub:
        `Take ${name} out of ${roomLabel} — they stay on today's roster and go back to the ` +
        `free list. ${seatName || "The other CRNA"} keeps the room.`,
      onPick: actions.onUnassign,
    },
    [BREAKS]: {
      icon: "☕",
      label: givingBreaks ? "Stop Giving Breaks" : "Give Breaks",
      // The old copy said "they stay on the free list", which is only true of
      // somebody who was already on it. Said to a CRNA holding a room it would
      // read as a promise to release them, which this option does not do.
      sub: givingBreaks
        ? "Clear the label — they stop being listed as a break-giver."
        : position === BENCH
          ? "They stay on the free list, labelled GIVING BREAKS."
          : `They keep ${roomLabel} and are listed as a break-giver — the AIC sees they have to be pulled out of a room first.`,
      onPick: actions.onToggleBreaks,
    },
    [HOME]: {
      icon: "🏠",
      label: "Home",
      sub: "Send them home — moves to the Home section.",
      onPick: actions.onHome,
    },
    [TRANSFER]: {
      icon: "🚗",
      label: "Transfer to Another Facility",
      sub: "Send them to another campus — tracked in the Traveling section until Arrival.",
      onPick: actions.onTransfer,
    },
  };
}
